// Re-export domain-specific types
export * from './picker';
export * from './monetization';
export * from './sdk';

// Media types
export enum MediaType {
  IMAGE = 'IMAGE',
  VIDEO = 'VIDEO',
  PHOTO = 'PHOTO',
}

export enum PlaybackMode {
  Instant = 'instant',
  Scheduled = 'scheduled',
}

export enum PreviewMediaType {
  IMAGE = 'image',
  VIDEO = 'video',
}

export interface BaseMedia {
  src: string;
  height?: number;
  width?: number;
}

export interface IVideo extends BaseMedia {
  poster?: string;
  duration?: number;
}

export interface IImage extends BaseMedia {
  alt?: string;
}

// Input types
export enum InputType {
  TEXT = 'text',
  IMAGE = 'image',
  VIDEO = 'video',
}

interface IBaseInput {
  type: InputType;
  id: string;
  title?: string;
}

export interface ITextInput extends IBaseInput {
  type: InputType.TEXT;
  value: string;
}

export interface IImageInput extends IBaseInput, Omit<IImage, 'src'> {
  type: InputType.IMAGE;
  src?: string | null;
  isLoading?: boolean;
  hasUploadError?: boolean;
}

export interface IVideoInput extends IBaseInput, Omit<IVideo, 'src'> {
  type: InputType.VIDEO;
  src?: string | null;
  isLoading?: boolean;
  hasUploadError?: boolean;
}

export type IInput = ITextInput | IImageInput | IVideoInput;
export type IMediaInput = IImageInput | IVideoInput;

// Type guards for input types
export function isTextInput(input: IInput): input is ITextInput {
  return input.type === InputType.TEXT;
}

export function isImageInput(input: IInput): input is IImageInput {
  return input.type === InputType.IMAGE;
}

export function isVideoInput(input: IInput): input is IVideoInput {
  return input.type === InputType.VIDEO;
}

export function isMediaInput(input: IInput): input is IMediaInput {
  return input.type === InputType.IMAGE || input.type === InputType.VIDEO;
}

// Library types
interface Media {
  width: number;
  height: number;
  url: string;
  poster?: string;
}

export interface LibraryItem {
  type: PreviewMediaType;
  id: number;
  slug: string;
  title?: string;
  description?: string;
  media: Media;
}

export interface LibraryTab {
  title: string;
  slug: string;
}

export interface Preset {
  video: { media: Media }[];
  image: { media: Media }[];
  textItems: IInput[];
  uploadItems: IInput[];
  flowId: string;
  slug?: string;
  type: PreviewMediaType;
  title?: string;
  description?: string;
  category?: string;
  categoryTitle?: string;
}

// Content types
export interface ContentFile {
  url: string;
  uuid: string;
  name: string;
  type?: MediaType;
  isLoading?: boolean;
  preview?: {
    url?: string;
    width?: number;
    height?: number;
  };
}

export type FlowMedia = {
  id: string;
  url: string;
  width?: number;
  height?: number;
  type: MediaType;
  driveUid?: string;
};

// Screen types
export type Screen = 'library' | 'create' | 'loading' | 'result' | 'files';

// Notification types
export enum NotificationType {
  SUCCESS = 'success',
  ERROR = 'error',
  WARNING = 'warning',
  INFO = 'info',
}

export interface NotificationConfig {
  title?: string;
  type?: NotificationType;
  projectId?: string;
  miniappPackageId?: string;
  actions?: Array<{ deeplink?: string; mobileDeeplink?: string }>;
}

// Project configuration types (for remote project loading)
export interface IDrive {
  folderName: string;
  toolId: string;
}

export interface IPreviewMedia {
  src: string;
  width?: number;
  height?: number;
  poster?: string;
  title?: string;
  description?: string;
  type: PreviewMediaType;
}

export interface ISideBarHeader {
  title: string;
  hasCloseIcon?: boolean;
  appIconName?: string;
  previewMedia?: IPreviewMedia;
}

export interface ISidebarFooter {
  primaryActionTitle?: string;
}

export interface ISidebar {
  header: ISideBarHeader;
  footer: ISidebarFooter;
}

export interface ILayout {
  sidebar: ISidebar;
}

export interface IProject {
  webhookId: string;
  resultType?: PreviewMediaType;
  drive: IDrive;
  layout: ILayout;
  inputs: IInput[];
}
